<script setup lang="ts">
import { computed, onMounted, type PropType, ref, watch } from 'vue'
import dayjs, { Dayjs } from 'dayjs'
import type { PDatePickerFastChoice } from '../date-picker/types'
import { PSpaceSize } from '../space/types'
import PSpace from '../space/PSpace.vue'
import PTimeInputField from '../fields/date/PTimeInputField.vue'
import { PTimeInputFieldIncrement } from '../fields/types'
import { usePrevioDS } from '../../composables/helpers'
import HeaderNavigation from './components/navigation/Navigation.vue'
import { provideDateTimePickerState } from './composables/DateTimePickerState'
import FastChoicesList from './components/fast-choices/FastChoicesList.vue'
import FastChoicesSelect from './components/fast-choices/FastChoicesSelect.vue'
import Carousel from './components/Carousel.vue'

const props = defineProps({
  min: {
    type: Object as PropType<Dayjs>,
    default: undefined,
  },
  max: {
    type: Object as PropType<Dayjs>,
    default: undefined,
  },
  fastChoices: {
    type: Array as PropType<PDatePickerFastChoice[]>,
    default: () => [],
  },
  disableClearable: {
    type: Boolean,
    default: false,
  },
  hideTimeField: {
    type: Boolean,
    default: false,
  },
  strict: {
    type: Boolean,
    default: false,
  },
  disable: {
    type: Boolean,
    default: false,
  },
  timeIncrement: {
    type: Number as PropType<PTimeInputFieldIncrement>,
    default: PTimeInputFieldIncrement.QUARTER,
  },
  defaultTime: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) return true
      return dayjs(value, 'HH:mm', true).isValid()
    },
    required: false,
  },
})

type Emits = {
  (e: 'select'): void
  (e: 'day-select'): void
}

const emits = defineEmits<Emits>()

const model = defineModel<Dayjs | null>({ default: null })

const ds = usePrevioDS()
const carouselComponent = ref<InstanceType<typeof Carousel>>()
const navigation = provideDateTimePickerState()

const hasFastChoices = computed<boolean>(() => props.fastChoices?.length > 0)

const onSubtract = (): void => {
  carouselComponent.value?.subtractMonth()
}

const onAdvance = (): void => {
  carouselComponent.value?.advanceMonth()
}

onMounted(() => {
  if (props.min) {
    navigation.setMin(props.min)
  }

  if (props.max) {
    navigation.setMax(props.max)
  }

  navigation.setDisableClearable(props.disableClearable)

  if (model.value) {
    navigation.setDate(model.value)
  }
})

watch(model, (value) => {
  if (!value) {
    return
  }

  navigation.navigateToCurrentDate(value)
})
</script>

<template>
  <div
    :class="[
      'ds-date-time-picker',
      { 'p-flex p-no-wrap p-items-start': ds.screen().isLaptopAndLarge },
    ]"
  >
    <div
      class="p-flex p-flex-direction-column p-no-wrap"
      :style="`height: ${ds.screen().isMobile ? `${390}px` : `${350}px`};`"
    >
      <fast-choices-select
        v-model="model"
        :fast-choices="fastChoices"
        :default-time="defaultTime"
        @select="emits('select')"
      />

      <p-space v-if="hasFastChoices" :mobile="PSpaceSize.MD" :laptop="PSpaceSize.ZERO" vertical />

      <header-navigation @subtract="onSubtract" @advance="onAdvance" />

      <carousel
        ref="carouselComponent"
        v-model="model"
        :default-time="defaultTime"
        @select="emits('day-select')"
      />

      <p-space :mobile="PSpaceSize.GROW" vertical />

      <div class="ds-date-time-picker__time-input-field">
        <p-time-input-field
          v-if="!hideTimeField"
          v-model="model"
          :min="min"
          :max="max"
          :disable-clearable="disableClearable"
          :increment="timeIncrement"
          :strict="strict"
          :disable="disable"
          cy="date-time-picker-input-field"
        />
      </div>
    </div>

    <p-space v-if="hasFastChoices" :mobile="PSpaceSize.ZERO" :laptop="PSpaceSize.SM" />

    <fast-choices-list
      v-if="ds.screen().isLaptopAndLarge"
      v-model="model"
      :fast-choices="fastChoices"
      :default-time="defaultTime"
      @select="emits('select')"
    />
  </div>
</template>

<style scoped lang="scss">
@import '../../assets/scss/shared/variables/ds.spacing';
@import '../../assets/scss/shared/mixins';

.ds-date-time-picker {
  &__time-input-field {
    @include desktop {
      max-width: 50% !important;
    }
    @include laptop {
      max-width: 50% !important;
    }
  }
}
</style>
